/**
 * Who may hold a role that carries GLOBAL_ADMIN (PREC-4636, decided by Andreas on 2026-10-10;
 * signalist.de added the same day, so user 3287 keeps ADMIN).
 *
 * The ADMIN role carries GLOBAL_ADMIN: every staff route and every cross-tenant support tool
 * opens for it. Customers got it because Signalist staff placed in their companies invited them
 * with it (users 4319 and 4320, 2026-09-29), so the rule is about the account, not the caller:
 * only an account whose e-mail domain is exactly one of these may hold such a role. The API
 * refuses the grant (`assertMayGrantRoles`, apps/api/src/roles/utils/role-grant.util.ts) and an
 * e-mail change that would leave the role on another domain; the Invite and Edit user modals
 * offer ADMIN only for these domains. One function, both sides.
 */
export const GLOBAL_ADMIN_EMAIL_DOMAINS: readonly string[] = [
	"signalist.io",
	"digital-now.io",
	"signalist.de",
];

/**
 * Whether an account with this e-mail may hold a role carrying GLOBAL_ADMIN: the part after the
 * single "@" equals one of {@link GLOBAL_ADMIN_EMAIL_DOMAINS}, case-insensitive. A subdomain or a
 * lookalike (`signalist.io.example.com`, `xsignalist.io`, `ops.signalist.io`) does not match, and
 * neither does an address that is missing, blank or has no single "@".
 */
export function mayHoldGlobalAdmin(email: string | null | undefined): boolean {
	const parts = (email ?? "").trim().toLowerCase().split("@");

	if (parts.length !== 2 || !parts[0]) {
		return false;
	}

	return GLOBAL_ADMIN_EMAIL_DOMAINS.includes(parts[1]);
}

/** The staff role's code and the permission that makes a holder Signalist staff. */
export const STAFF_ROLE_CODE = "ADMIN";
export const GLOBAL_ADMIN_PERMISSION = "GLOBAL_ADMIN";

/** The part of a role these rules read; `permissions` must be loaded with the role. */
export interface RoleWithPermissions {
	code: string;
	permissions?: readonly { code: string }[] | null;
}

/**
 * Whether holding this role makes a user Signalist staff: the ADMIN role, or any role carrying
 * GLOBAL_ADMIN. A role read without its permissions counts as staff (fail closed): an unreadable
 * role must narrow who may grant it, never widen it. The API's grant helper and the web's Invite
 * and Edit user modals both ask this (PREC-4634, PREC-4636).
 */
export function roleCarriesGlobalAdmin(role: RoleWithPermissions): boolean {
	if (role.code === STAFF_ROLE_CODE || !role.permissions) {
		return true;
	}

	return role.permissions.some((permission) => permission.code === GLOBAL_ADMIN_PERMISSION);
}

/**
 * The permission codes `role` holds that are not in `held`: a role above its granter's own
 * (PREC-4634: production's USER role holds INVITE_USER, so a seat could invite a COMPANY_ADMIN).
 */
export function permissionsMissingFor(role: RoleWithPermissions, held: Iterable<string>): string[] {
	const heldCodes = new Set(held);

	return (role.permissions ?? []).map((permission) => permission.code).filter((code) => !heldCodes.has(code));
}

/** The account a GLOBAL_ADMIN role would be granted to. */
export interface GlobalAdminGrantee {
	email?: string | null;
	emailVerifiedAt?: Date | string | null;
	/**
	 * Set when the account was created by an invitation (`user.invitedAt`, stamped only by
	 * `POST users/invite`; a self-signup never has it). Such an account never self-registered, so
	 * it may hold the role before it confirms: Signalist's "Log in as" support aliases in customer
	 * companies are invited, never set a password and never confirm (PREC-4636, round 2).
	 */
	invitedAt?: Date | string | null;
	/**
	 * The grant is part of the invitation that creates this account. The invitee can sign in only
	 * through the set-password link mailed to that address, which stamps `emailVerifiedAt`, so the
	 * invitation itself is the verification path (PREC-4636).
	 */
	viaInvitation?: boolean;
}

/**
 * Whether a role carrying GLOBAL_ADMIN may be granted to this account: a staff-domain address
 * (`mayHoldGlobalAdmin`) whose account is confirmed, or was created by an invitation (now, or
 * earlier: `invitedAt`). A self-registered staff-domain account that never confirmed may not get it.
 */
export function mayBeGrantedGlobalAdmin(account: GlobalAdminGrantee): boolean {
	return mayHoldGlobalAdmin(account.email) && staffGranteeQualifies(account);
}

/** The confirmation half of `mayBeGrantedGlobalAdmin`: confirmed, or never self-registered. */
export function staffGranteeQualifies(account: GlobalAdminGrantee): boolean {
	return account.viaInvitation === true || !!account.emailVerifiedAt || !!account.invitedAt;
}
